// Generic, reusable sortable + searchable + filterable data table used across the
// Employee Profile sub-sections (Coaching History, All History, Onboarding Checklist).
import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TextInput, Pressable, ScrollView, ViewStyle } from 'react-native';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';

export interface ProfileTableColumn<T> {
  key: string;
  label: string;
  /** Relative flex weight — columns fill the full table width proportionally. */
  flex: number;
  sortable?: boolean;
  filterable?: boolean;
  numeric?: boolean;
  render?: (row: T) => React.ReactNode;
  getValue?: (row: T) => string;
  /** Optional per-row style applied to the cell container (e.g. highlighted background). */
  getCellStyle?: (row: T) => ViewStyle | undefined;
}

interface ProfileDataTableProps<T extends { id: string }> {
  columns: ProfileTableColumn<T>[];
  rows: T[];
  searchPlaceholder?: string;
  searchFields?: (keyof T)[];
  emptyLabel?: string;
  /**
   * "plan" renders the mockup's borderless-toolbar table: rounded bordered wrapper,
   * uppercase header labels without sort/filter glyphs and roomier rows.
   */
  variant?: 'default' | 'plan';
}

function ProfileDataTable<T extends { id: string }>({
  columns,
  rows,
  searchPlaceholder = 'Search…',
  searchFields,
  emptyLabel = 'No records found.',
  variant = 'default',
}: ProfileDataTableProps<T>) {
  const isPlan = variant === 'plan';
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, Set<string>>>({});

  const getCellText = (row: T, col: ProfileTableColumn<T>): string => {
    if (col.getValue) return col.getValue(row);
    const v = (row as any)[col.key];
    return v === null || v === undefined ? '' : String(v);
  };

  const filterOptions = useMemo(() => {
    const map: Record<string, string[]> = {};
    columns.forEach(col => {
      if (!col.filterable) return;
      const vals = new Set<string>();
      rows.forEach(r => {
        const v = getCellText(r, col);
        if (v) vals.add(v);
      });
      map[col.key] = Array.from(vals).sort();
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns]);

  const filteredRows = useMemo(() => {
    let out = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(r => {
        const fields = searchFields && searchFields.length ? searchFields : (Object.keys(r as any) as (keyof T)[]);
        return fields.some(f => String((r as any)[f] ?? '').toLowerCase().includes(q));
      });
    }
    Object.entries(activeFilters).forEach(([key, selected]) => {
      if (!selected || selected.size === 0) return;
      const col = columns.find(c => c.key === key);
      if (!col) return;
      out = out.filter(r => selected.has(getCellText(r, col)));
    });
    if (sortKey) {
      const col = columns.find(c => c.key === sortKey);
      out = [...out].sort((a, b) => {
        const av = col ? getCellText(a, col) : '';
        const bv = col ? getCellText(b, col) : '';
        const cmp = col?.numeric ? Number(av) - Number(bv) : av.localeCompare(bv);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, searchFields, activeFilters, sortKey, sortDir, columns]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleFilterValue = (colKey: string, value: string) => {
    setActiveFilters(prev => {
      const next = { ...prev };
      const set = new Set(next[colKey] || []);
      if (set.has(value)) set.delete(value); else set.add(value);
      next[colKey] = set;
      return next;
    });
  };

  return (
    <View>
      {!isPlan && (
        <View style={styles.toolbar}>
          <TextInput
            style={styles.searchInput}
            placeholder={searchPlaceholder}
            placeholderTextColor={AdminColors.textMuted}
            value={search}
            onChangeText={setSearch}
          />
          <Text style={styles.countText}>{filteredRows.length} record{filteredRows.length === 1 ? '' : 's'}</Text>
        </View>
      )}
      <View style={[styles.table, isPlan && styles.tablePlan]}>
        <View style={[styles.headerRow, isPlan && styles.headerRowPlan]}>
          {columns.map(col => (
            <View key={col.key} style={[styles.headerCell, isPlan && styles.headerCellPlan, { flex: col.flex }]}>
                <Pressable
                  style={styles.headerPressable}
                  onPress={() => !isPlan && col.sortable && toggleSort(col.key)}
                >
                  <Text style={[styles.headerText, isPlan && styles.headerTextPlan]} numberOfLines={1}>{col.label}</Text>
                  {!isPlan && col.sortable && (
                    <Text style={styles.sortArrow}>{sortKey === col.key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</Text>
                  )}
                </Pressable>
              {!isPlan && col.filterable && (
                <Pressable onPress={() => setOpenFilterKey(openFilterKey === col.key ? null : col.key)} hitSlop={6}>
                  <Text style={styles.filterIcon}>⏷</Text>
                </Pressable>
              )}
              {openFilterKey === col.key && (
                <View style={styles.filterMenu}>
                  <ScrollView style={{ maxHeight: 200 }}>
                    {(filterOptions[col.key] || []).map(opt => {
                      const checked = activeFilters[col.key]?.has(opt);
                      return (
                        <Pressable key={opt} style={styles.filterOption} onPress={() => toggleFilterValue(col.key, opt)}>
                          <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                            {checked && <Text style={styles.checkboxTick}>✓</Text>}
                          </View>
                          <Text style={styles.filterOptionText} numberOfLines={1}>{opt}</Text>
                        </Pressable>
                      );
                    })}
                  </ScrollView>
                </View>
              )}
            </View>
          ))}
        </View>
        {filteredRows.length === 0 ? (
          <View style={styles.emptyRow}>
            <Text style={styles.emptyText}>{emptyLabel}</Text>
          </View>
        ) : (
          filteredRows.map((row, idx) => (
            <View
              key={row.id}
              style={[
                styles.dataRow,
                isPlan && styles.dataRowPlan,
                isPlan && idx === filteredRows.length - 1 && styles.dataRowLastPlan,
              ]}
            >
              {columns.map(col => (
                <View
                  key={col.key}
                  style={[styles.dataCell, isPlan && styles.dataCellPlan, { flex: col.flex }, col.getCellStyle?.(row)]}
                >
                  {col.render ? col.render(row) : (
                    <Text style={[styles.dataCellText, isPlan && styles.dataCellTextPlan]} numberOfLines={2}>
                      {getCellText(row, col)}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          ))
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  table: {
    width: '100%',
  },
  tablePlan: {
    borderWidth: 1,
    borderColor: AdminColors.gray25,
    borderRadius: 10,
    backgroundColor: AdminColors.bgSurface,
    overflow: 'hidden',
  },
  headerRowPlan: {
    backgroundColor: AdminColors.bgSurface,
    borderBottomWidth: 2,
    borderColor: AdminColors.borderTable,
  },
  headerCellPlan: {
    paddingVertical: 11,
    paddingHorizontal: 12,
  },
  headerTextPlan: {
    fontSize: 10,
    lineHeight: 14,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  dataRowPlan: {
    borderColor: AdminColors.gray25,
  },
  dataRowLastPlan: {
    borderBottomWidth: 0,
  },
  dataCellPlan: {
    paddingVertical: 11,
    paddingHorizontal: 12,
  },
  dataCellTextPlan: {
    fontSize: 14,
    lineHeight: 20,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 12,
  },
  searchInput: {
    flex: 1,
    maxWidth: 320,
    height: 36,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    paddingHorizontal: 12,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  countText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textSecondary,
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: AdminColors.borderTable,
    backgroundColor: AdminColors.blue25,
  },
  headerCell: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 10,
    gap: 4,
    minWidth: 0,
  },
  headerPressable: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
  },
  headerText: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: AdminColors.textSecondary,
  },
  sortArrow: {
    fontSize: 10,
    color: AdminColors.textMuted,
  },
  filterIcon: {
    fontSize: 10,
    color: AdminColors.textMuted,
  },
  filterMenu: {
    position: 'absolute' as any,
    top: '100%' as any,
    left: 0,
    zIndex: 20,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    minWidth: 180,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: AdminColors.blue700,
    borderColor: AdminColors.blue700,
  },
  checkboxTick: {
    color: '#FFFFFF',
    fontSize: 10,
  },
  filterOptionText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    color: AdminColors.textPrimary,
    flexShrink: 1,
  },
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: AdminColors.borderTable,
  },
  dataCell: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    justifyContent: 'center',
    minWidth: 0,
  },
  dataCellText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  emptyRow: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textMuted,
  },
});

export default ProfileDataTable;
